import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import {
  COLUMN_WIDTH,
  DEFAULT_CLUSTER_LENGTH,
  DEFAULT_PLANABLE_OBJECT,
  DEFAULT_PLANNING_META,
  PLANNING_DRAG_THROTTHLE,
} from "./learningObjectivesConstants";

// Helper to find index in week list
export const findWeekIndex = (week, year) => {
  const learningObjectivesStore = useLearningObjectivesStore();
  const { weeks } = storeToRefs(learningObjectivesStore);

  return weeks.value.findIndex((w) => w.week === week && w.year === year);
};

export const totalWeeks = () => {
  const learningObjectivesStore = useLearningObjectivesStore();
  const { weeks } = storeToRefs(learningObjectivesStore);

  return weeks.value.length;
};

export const findWeekByIndex = (index) => {
  const learningObjectivesStore = useLearningObjectivesStore();
  const { weeks } = storeToRefs(learningObjectivesStore);

  return weeks.value[index];
};

export class Planable {
  start_week;
  start_year;
  end_week;
  end_year;
  school_year;
  planningMeta;

  constructor(data = {}) {
    this.start_week = data.start_week || DEFAULT_PLANABLE_OBJECT.start_week;
    this.start_year = data.start_year || DEFAULT_PLANABLE_OBJECT.start_year;
    this.end_week = data.end_week || DEFAULT_PLANABLE_OBJECT.end_week;
    this.end_year = data.end_year || DEFAULT_PLANABLE_OBJECT.end_year;
    this.school_year = data.school_year || DEFAULT_PLANABLE_OBJECT.school_year;

    this.planningMeta = {
      isResizing: data.isResizing || DEFAULT_PLANNING_META.isResizing,
      startX: data.startX || DEFAULT_PLANNING_META.startX,
      handleTarget: data.handleTarget || DEFAULT_PLANNING_META.handleTarget,
      handleX: data.handleX || DEFAULT_PLANNING_META.handleX,
      throttledResize:
        data.throttledResize || DEFAULT_PLANNING_META.throttledResize,
      resizingPart: data.resizingPart || DEFAULT_PLANNING_META.resizingPart,
      manager: data.manager || DEFAULT_PLANNING_META.manager,
      canDrag: data.canDrag || DEFAULT_PLANNING_META.canDrag,
      isDraggable: data.isDraggable || DEFAULT_PLANNING_META.isDraggable,
      initialDragState: {},
      weekIndexes: [],
      rowIndex: data.row_index ?? DEFAULT_PLANNING_META.rowIndex,
      groupId: data.groupId ?? DEFAULT_PLANNING_META.groupId,
      groupIndex: data.groupIndex ?? DEFAULT_PLANNING_META.groupIndex,
    };

    this.generateWeekIndexes();
  }

  updateRowIndex(rowIndex = null) {
    if (isNaN(parseInt(rowIndex))) {
      throw new Error("Row index is required to update the row index");
    }

    this.planningMeta.rowIndex = rowIndex;
  }

  updateGroupIndex(groupIndex = null) {
    if (isNaN(parseInt(groupIndex))) {
      throw new Error("Group index is required to update the row index");
    }

    const group = this.getManager().findGridGroupIdByIndex(groupIndex);

    this.lesson_class = group.id;
    this.subject_shortage = group.meta.subject;
    this.planningMeta.groupId = group.id;
    this.planningMeta.groupIndex = groupIndex;
  }

  isDraggable() {
    return !!this.planningMeta.isDraggable;
  }

  getManager() {
    return this.planningMeta.manager;
  }

  isResizing() {
    return this.planningMeta.isResizing;
  }

  startDelta(event, handle) {
    const { target, clientX } = event;

    this.planningMeta.isResizing = true;
    this.planningMeta.startX = clientX;
    this.planningMeta.handleTarget = handle;

    // We need to set the target and the x position of the handle to the middle of the target
    this.planningMeta.handleX = Math.round(
      target.getBoundingClientRect().left + target.clientWidth / 2,
    );

    // Set the modelId to the dragged period and start the drag
    this.planningMeta.manager.startDelta(this, this.planningMeta.startX);

    this.planningMeta.resizingPart = target.dataset.drag;

    this.planningMeta.throttledResize = throttle(
      this.onResize.bind(this),
      PLANNING_DRAG_THROTTHLE,
      {
        leading: true,
        trailing: false,
      },
    );

    document.addEventListener("mousemove", this.planningMeta.throttledResize);
    document.addEventListener("mouseup", this.stopResize.bind(this));
  }

  onResize(event) {
    if (!this.isResizing()) {
      return;
    }

    // On resize we need to calculate the new position of the handle
    this.planningMeta.handleX = Math.round(
      this.planningMeta.handleTarget.getBoundingClientRect().left +
        this.planningMeta.handleTarget.clientWidth / 2,
    );

    if (this.planningMeta.handleX === 0) {
      return;
    }

    const deltaX = event.clientX - this.planningMeta.handleX;
    const changeInColumns = Math.round(deltaX / COLUMN_WIDTH);

    this.planningMeta.manager.updateDelta(
      this,
      changeInColumns,
      this.planningMeta.resizingPart,
      event.clientX,
    );
  }

  stopResize() {
    document.removeEventListener(
      "mousemove",
      this.planningMeta.throttledResize,
    );
    document.removeEventListener("mouseup", this.stopResize);

    this.planningMeta.manager.endDelta(this.id);

    this.planningMeta.resizingPart = null;
    this.planningMeta.isResizing = false;
  }

  getStartWeekIndex() {
    return this.getWeekIndex(this.start_week, this.start_year);
  }

  getEndWeekIndex() {
    return this.getWeekIndex(this.end_week, this.end_year);
  }

  // Helper to find index in week list
  getWeekIndex(week, year) {
    return findWeekIndex(week, year);
  }

  getWeekByIndex(index) {
    return findWeekByIndex(index);
  }

  updateStartWeek(week, year) {
    this.start_week = week;
    this.start_year = year;

    this.generateWeekIndexes();
  }

  updateEndWeek(week, year) {
    this.end_week = week;
    this.end_year = year;

    this.generateWeekIndexes();
  }

  generateWeekIndexes() {
    const startWeekIndex = this.getStartWeekIndex();
    const endWeekIndex = this.getEndWeekIndex();
    const length = endWeekIndex - startWeekIndex + 1;
    const weekIndexes = Array.from({ length }, (_, i) => startWeekIndex + i);

    this.duration = weekIndexes.length;

    this.planningMeta.weekIndexes = weekIndexes;

    this.setInitialDragState();
  }

  setInitialDragState() {
    const { initialDragState, weekIndexes } = this.planningMeta;

    initialDragState.weekIndex = weekIndexes[0];
    initialDragState.duration = this.duration;
    initialDragState.rowIndex = this.planningMeta.rowIndex;
  }

  generateTempId() {
    // Bit ugly but it works, only a temporary solution, backend will create the ID after saving
    return new Date().getTime();
  }

  onDragStart() {
    const {
      planningMeta: { rowIndex, weekIndexes },
    } = this;

    this.getManager().removeItemFromGrid(rowIndex, weekIndexes, this);

    // Set dragging state on the cluster group
    this.getManager().setDraggingStateOnClusterGroup(this.lesson_class, true);

    return this;
  }

  hasAllWeeksFilled() {
    const { start_week, start_year, end_week, end_year } = this;

    return [start_week, start_year, end_week, end_year].every(
      (value) => value !== null,
    );
  }

  onDragDrop(
    event,
    {
      rowIndex = null,
      weekIndex = null,
      groupIndex = null,
      duration = null,
    } = {},
  ) {
    if (event) {
      const {
        location: { current },
      } = event;

      // if not dropped in a valid location,
      // return the cluster to the grid in the original spot
      if (current.dropTargets.length === 0) {
        this.getManager().addGridItem(this);

        // Set dragging state on the cluster group
        return this.getManager().setDraggingStateOnClusterGroup(
          this.lesson_class,
          false,
        );
      }

      const {
        rowIndex: _rowIndex,
        weekIndex: _weekIndex,
        groupIndex: _groupIndex,
      } = current.dropTargets[0].data;

      rowIndex = _rowIndex;
      weekIndex = _weekIndex;
      groupIndex = _groupIndex;
    }

    if (groupIndex !== null || groupIndex !== undefined) {
      // Update the current items group index
      this.updateGroupIndex(groupIndex);
    }

    // A caller (e.g. drag-to-size on the add button) can request an explicit
    // duration. Otherwise a brand-new cluster falls back to the default length.
    if (duration !== null) {
      this.duration = duration;
    } else if (!this.hasAllWeeksFilled()) {
      this.duration = DEFAULT_CLUSTER_LENGTH;
    }

    // Calculate the max duration of the cluster
    const maxDuration = totalWeeks() - weekIndex;

    this.duration = Math.min(this.duration, maxDuration);

    this.getManager().addGridItem(this, rowIndex, weekIndex, groupIndex);

    // Update the current items row index
    this.updateRowIndex(rowIndex);

    // Update the current items start week
    const { week: startWeek, year: startYear } = this.getWeekByIndex(weekIndex);
    const { week: endWeek, year: endYear } = this.getWeekByIndex(
      weekIndex + (this.duration - 1),
    );

    this.updateEndWeek(endWeek, endYear);
    this.updateStartWeek(startWeek, startYear);

    // Set dragging state on the cluster group
    this.getManager().setDraggingStateOnClusterGroup(this.lesson_class, false);
  }

  setDraggable(element, handle) {
    if (!this.isDraggable()) {
      return;
    }

    draggable({
      element: element,
      dragHandle: handle,
      onDragStart: (event) => this.onDragStart(event),
      onDrop: (event) => this.onDragDrop(event),
      getInitialData: () => this?.getAdditionalData(),
    });
  }
}
